/**
 * 올인원 — 여권 탭 (레거시 '여권업무' fp-card-grid 레이아웃 채택)
 * docs/tools_all.md §7
 *
 * 레이아웃 (legacy PassportRegisterPage 와 동일):
 *  - 상단: 요약 바 (가족 N명 · 전체 여권 N장)
 *  - 카드 그리드 (3열): 각 카드는 여권 1장
 *      · 우상단 ✕  → cancelPassport
 *      · 이미지(passport_product_front_img) + 좌하단 지점명 라벨
 *      · 하단 콤보(가족 선택) → assignPassport (소유자 변경)
 *  - 우측 사이드바: 신규 여권 발급 폼 (대상 가족 선택 + 발급일 + 발급 버튼)
 *  - 만료포함 토글 + 할당이력 Drawer
 */

import { createPortal } from 'react-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Empty, Drawer, Spin, Select, DatePicker, Typography,
  Popconfirm, App as AntApp, message as messageApi,
} from 'antd';
import {
  HistoryOutlined, ReloadOutlined, SafetyCertificateOutlined, SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  registerPassport, assignPassport, cancelPassport,
  getAllMemberPassports, getPassportAssignmentLogs,
} from '../../services/api';
import '../../pages/PassportRegisterPage.css';
import './PassportTab.css';
import '../HistoryPanel.css';
import { passportHistoryRows } from './passportHistoryRows';

const { Text } = Typography;

const getDisplayName = (m) =>
  m?.nickname || m?.name || (m?.member_type === 'CHILD' ? '자녀' : '회원');

const shortStoreName = (p) =>
  (p?.store_name || `지점${p?.store_id ?? ''}`).replace(/^주렁주렁\s*/, '').replace(/점$/, '');

const isActivePassport = (p) => {
  if (!p) return false;
  if (p.status !== 'ACTIVE') return false;
  const exp = new Date(p.expires_at || p.expire_date || 0);
  return exp.getTime() > Date.now();
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};


/**
 * 여권 취소 확인 본문 — 기획서 §4.12.6 (가) 확정 문구.
 *
 * 🔴 **파기를 뜻하는 낱말을 쓰지 않습니다.** 실제로 지워지는 행이 하나도 없습니다 —
 *    여권은 `CANCELLED`, 쿠폰은 `recalled`, 스탬프는 `recalled_at` 마킹입니다.
 *    v1.10 이전 문구는 여권·쿠폰·스탬프 3항목 모두를 사실과 다르게 안내했습니다(E22).
 *
 * 🔴 **"사용 완료된 쿠폰은 회수되지 않습니다" 는 필수 문장입니다**(C20). 회수는 `usable`
 *    한정이라 이미 사용된 쿠폰은 취소되지 않는데, 이 한 줄이 빠지면 운영자가
 *    "전부 취소됨" 으로 오인해 정산 분쟁이 납니다.
 *
 * 🔴 금액·건수를 여기 상수로 넣지 않습니다 — 결과 수치는 취소 응답값으로만 표시합니다
 *    (`handleCancel` 참조 / CLAUDE.md 주의 8).
 *
 * 🔴 이 화면(주렁은행 올인원)과 QR 스캔 여권취소 화면은 **같은 취소 코어**를 타므로
 *    문구도 같은 사실을 말해야 합니다(§4.12.1).
 */
const CANCEL_DESCRIPTION = (
  <div style={{ maxWidth: 320, lineHeight: 1.6 }}>
    <div>이 여권이 &apos;취소&apos; 상태가 되고, 아래가 함께 회수됩니다.</div>
    <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
      <li>미사용 쿠폰 <Text type="secondary">(사용 완료된 쿠폰은 회수되지 않습니다)</Text></li>
      <li>여권 구매 주화</li>
      <li>이 여권의 스탬프</li>
    </ul>
    <div style={{ fontWeight: 600 }}>되돌릴 수 없습니다.</div>
  </div>
);

export default function PassportTab({
  scannedMember, primary, family, historyActionTarget, onHistoryOpenChange,
  refresh, markDirty, beginAction, endAction, onLog,
}) {
  const { modal } = AntApp.useApp();
  const historyPending = useRef(false);

  // family/scannedMember 로부터 타겟 목록을 만들되 member_id 중복 제거(레이스 방어)
  const targets = useMemo(() => {
    const base = family.length ? family : (scannedMember ? [scannedMember] : []);
    const seen = new Set();
    const out = [];
    for (const m of base) {
      if (!m?.member_id || seen.has(m.member_id)) continue;
      seen.add(m.member_id);
      out.push(m);
    }
    return out;
  }, [family, scannedMember]);
  const parentId = primary?.member_id || scannedMember?.member_id;

  // 가족별 여권 풀-로드 (활성 + 만료/취소 포함)
  const [familyPassports, setFamilyPassports] = useState({}); // memberId -> passports[]
  const [loading, setLoading] = useState(false);
  const [busyPassportId, setBusyPassportId] = useState(null);

  // 신규 발급 폼
  const [newOwnerId, setNewOwnerId] = useState(null);
  const [newIssueDate, setNewIssueDate] = useState(dayjs());
  const [registering, setRegistering] = useState(false);
  const registerConfirmOpen = useRef(false);

  // 할당 이력 Drawer
  const [logsDrawer, setLogsDrawer] = useState({ open: false, loading: false, logs: [], error: null });
  useEffect(() => {
    onHistoryOpenChange?.(logsDrawer.open);
    return () => onHistoryOpenChange?.(false);
  }, [logsDrawer.open, onHistoryOpenChange]);

  const storeId = localStorage.getItem('store_id') || '3';

  // reload 동시성 가드: 가장 마지막에 시작된 호출의 결과만 반영
  const reloadSeqRef = useRef(0);
  const reload = useCallback(async () => {
    if (!targets.length) return;
    const seq = ++reloadSeqRef.current;
    setLoading(true);
    try {
      const map = {};
      await Promise.all(targets.map(async (m) => {
        try {
          const res = await getAllMemberPassports(m.member_id, true);
          map[m.member_id] = res?.passports || res?.data?.passports || [];
        } catch {
          map[m.member_id] = [];
        }
      }));
      // 더 새로운 reload 가 시작되었으면 결과 폐기 (race 방어)
      if (seq !== reloadSeqRef.current) return;
      setFamilyPassports(map);
    } finally {
      if (seq === reloadSeqRef.current) setLoading(false);
    }
  }, [targets]);

  // 가족이 바뀌면 재로드 (식별 직후 + refresh 후 markDirty 시점에는 family 객체가 새 ref)
  useEffect(() => { reload(); }, [reload]);

  // 신규 발급 폼: 기본 대상은 보호자(=parentId)
  useEffect(() => {
    if (!targets.length) { setNewOwnerId(null); return; }
    if (!targets.some((m) => m.member_id === newOwnerId)) {
      setNewOwnerId(parentId || targets[0].member_id);
    }
  }, [targets, parentId, newOwnerId]);

  // 카드 데이터: 가족 전체의 활성 여권을 평탄화 + passport_id 기준 dedup
  const cardPassports = useMemo(() => {
    const out = [];
    const seen = new Set();
    targets.forEach((m) => {
      const list = familyPassports[m.member_id] || [];
      list.forEach((p) => {
        if (!isActivePassport(p)) return;
        if (!p?.passport_id || seen.has(p.passport_id)) return;
        seen.add(p.passport_id);
        out.push({
          ...p,
          current_owner_id: m.member_id,
          current_owner_name: getDisplayName(m),
        });
      });
    });
    return out;
  }, [targets, familyPassports]);

  if (!scannedMember) return <Empty description="회원을 검색 해 주세요" />;

  const familyOptions = targets.map((m) => ({
    value: m.member_id,
    label: `${getDisplayName(m)}${m.member_type === 'CHILD' ? ' (자녀)' : ''}`,
  }));

  const finishAction = async (label) => {
    onLog?.(label);
    await Promise.all([reload(), refresh?.()]);
    markDirty('passport');
  };

  const handleAssign = async (passport, toMemberId) => {
    if (!toMemberId || toMemberId === passport.current_owner_id) return;
    const target = targets.find((m) => m.member_id === toMemberId);
    const actionId = `pp-asn-${passport.passport_id}`;
    setBusyPassportId(passport.passport_id);
    beginAction(actionId);
    try {
      await assignPassport(passport.passport_id, toMemberId);
      await finishAction(`✅ [${passport.current_owner_name} → ${getDisplayName(target)}] 여권 할당`);
      messageApi.success('할당 완료');
    } catch (err) {
      messageApi.error(err?.message || '할당 실패');
    } finally {
      endAction(actionId);
      setBusyPassportId(null);
    }
  };

  const confirmAssign = (passport, toMemberId) => {
    if (!toMemberId || toMemberId === passport.current_owner_id) return;
    const target = targets.find((m) => m.member_id === toMemberId);
    modal.confirm({
      title: '여권 소유자를 변경할까요?',
      className: 'zb-withdraw-confirm passport-confirm',
      width: 760,
      maskClosable: false,
      content: <dl className="zb-confirm-details">
        <div><dt>현재 소유자</dt><dd>{passport.current_owner_name}</dd></div>
        <div><dt>양도받을 가족</dt><dd>{getDisplayName(target)}</dd></div>
        <div><dt>유효기간</dt><dd>{formatDate(passport.expires_at || passport.expire_date)}까지</dd></div>
        <p className="zb-confirm-note">선택한 가족에게 이 여권의 소유자가 변경됩니다. 대상자를 확인해 주세요.</p>
      </dl>,
      okText: '양도하기',
      cancelText: '취소',
      centered: true,
      onOk: () => handleAssign(passport, toMemberId),
    });
  };

  const handleCancel = async (passport) => {
    const actionId = `pp-cnl-${passport.passport_id}`;
    setBusyPassportId(passport.passport_id);
    beginAction(actionId);
    try {
      const res = await cancelPassport(passport.passport_id);
      // 🔴 회수 수치는 **서버 응답값**을 그대로 렌더합니다. 화면이 계산하거나 상수로
      //    채우면 CLAUDE.md 주의 8(적립·회수 수치 하드코딩 금지) 위반입니다.
      //    (응답 = success_response 봉투이므로 `res.data`. 방어적으로 res 도 봅니다.)
      const d = res?.data || res || {};
      const recalled = d.recalled_coupons ?? 0;
      const coins = d.reversed_coins ?? 0;
      const stamps = d.recalled_stamps ?? 0;
      await finishAction(
        `✅ [${passport.current_owner_name}] 여권 취소 — 쿠폰 ${recalled}건 · 주화 ${coins} · 스탬프 ${stamps}개 회수`
      );
      messageApi.success(`취소 완료 — 쿠폰 ${recalled} · 주화 ${coins} · 스탬프 ${stamps} 회수`);
    } catch (err) {
      messageApi.error(err?.message || '취소 실패');
    } finally {
      endAction(actionId);
      setBusyPassportId(null);
    }
  };

  const handleRegister = async () => {
    if (!newOwnerId || registering) return;
    const target = targets.find((m) => m.member_id === newOwnerId);
    const actionId = `pp-reg-${newOwnerId}`;
    setRegistering(true);
    beginAction(actionId);
    try {
      const issueDate = newIssueDate.format('YYYY-MM-DD');
      const res = await registerPassport({ memberId: newOwnerId, storeId, issueDate });
      if (res?.success === false) throw new Error(res.message || '발급 실패');
      await finishAction(`✅ [${getDisplayName(target)}] 여권 발급`);
      messageApi.success(`${getDisplayName(target)} 여권 발급 완료`);
    } catch (err) {
      messageApi.error(err?.message || '발급 실패');
    } finally {
      endAction(actionId);
      setRegistering(false);
    }
  };

  const confirmRegister = () => {
    if (!newOwnerId || registering || registerConfirmOpen.current) return;
    const target = targets.find(m => m.member_id === newOwnerId);
    if (!target || !newIssueDate?.isValid()) return;
    registerConfirmOpen.current = true;
    modal.confirm({
      title: '여권을 발급하시겠습니까?',
      className: 'zb-withdraw-confirm passport-confirm',
      centered: true, width: 760, maskClosable: false,
      content: <dl className="zb-confirm-details">
        <div><dt>발급 대상</dt><dd>{getDisplayName(target)}</dd></div>
        <div><dt>발급일</dt><dd>{newIssueDate.format('YYYY년 M월 D일')}</dd></div>
        <p className="zb-confirm-note">결제가 완료되었는지 확인해 주세요. 확인한 대상자에게 여권을 발급합니다.</p>
      </dl>,
      okText: '발급하기', cancelText: '돌아가기',
      onCancel: () => { registerConfirmOpen.current = false; },
      onOk: async () => {
        try { await handleRegister(); }
        finally { registerConfirmOpen.current = false; }
      },
    });
  };

  const openLogsDrawer = async () => {
    if (historyPending.current) return;
    historyPending.current = true;
    setLogsDrawer({ open: true, loading: true, logs: [], error: null });
    try {
      const ids = new Set(targets.map((m) => m.member_id));
      const results = await Promise.all([...ids].map((id) => getPassportAssignmentLogs(id).catch(() => null)));
      const merged = [];
      const seen = new Set();
      for (const r of results) {
        if (!r) continue;
        const logs = r.logs || r.data?.logs || [];
        for (const log of logs) {
          if (seen.has(log.id)) continue;
          seen.add(log.id);
          merged.push(log);
        }
      }
      merged.sort((a, b) => new Date(b.assigned_at || 0) - new Date(a.assigned_at || 0));
      setLogsDrawer({ open: true, loading: false, logs: merged, error: null });
    } catch (err) {
      setLogsDrawer({ open: true, loading: false, logs: [], error: err?.message || '조회 실패' });
    } finally { historyPending.current = false; }
  };

  const renderCard = (passport) => {
    const imgSrc = passport.passport_product_front_img
      ? `/passport_img/${passport.passport_product_front_img}`
      : null;
    const storeLabel = shortStoreName(passport);
    const busy = busyPassportId === passport.passport_id;
    const expDate = passport.expires_at?.slice(0, 10) || passport.expire_date?.slice(0, 10) || '-';

    return (
      <div key={passport.passport_id} className="fp-card">
        <div className="passport-card-heading">
          <div>
            <span className="passport-card-eyebrow">현재 소유자</span>
            <strong className="passport-card-owner">{passport.current_owner_name}</strong>
          </div>
          <Popconfirm
            title="여권 취소"
            description={CANCEL_DESCRIPTION}
            okText="여권 취소 실행" cancelText="닫기" okButtonProps={{ danger: true }}
            onConfirm={() => handleCancel(passport)}
          >
            <button
              type="button"
              className="fp-card-delete"
              disabled={busy}
              title="여권 취소"
            >여권 취소</button>
          </Popconfirm>
        </div>

        <div className="fp-card-image">
          {imgSrc ? (
            <img src={imgSrc} alt={`${storeLabel} 여권`} decoding="async" draggable={false} />
          ) : (
            <div className="fp-card-placeholder">
              <span>{storeLabel}</span>
            </div>
          )}
          <span className="fp-card-store-label">{storeLabel}</span>
        </div>

        <div className="fp-card-bottom">
          <div className="passport-transfer-title">
            <SwapOutlined /> 다른 가족에게 양도
          </div>
          <div className="passport-transfer-help">가족을 고른 뒤 확인하면 소유자가 변경됩니다.</div>
          <div
            className="passport-transfer-select-wrap"
          >
            <Select
              className="fp-card-select-antd"
              value={passport.current_owner_id}
              disabled={busy}
              onChange={(val) => confirmAssign(passport, val)}
              options={familyOptions}
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              popupClassName="passport-family-popup"
              listHeight={288}
              placement="bottomLeft"
              dropdownStyle={{ minWidth: 260, maxWidth: 'calc(100vw - 24px)' }}
              showSearch={false}
              inputReadOnly
              virtual={false}
            />
          </div>
          <div className="fp-card-meta">
            <Text><span>유효기간</span><strong>{expDate}까지</strong></Text>
          </div>
        </div>

        {busy && (
          <div className="fp-card-busy-overlay"><Spin size="small" /></div>
        )}
      </div>
    );
  };

  return (
    <div className="passport-tab passport-tab-v2">
      {historyActionTarget && createPortal(<Button icon={<HistoryOutlined />} onClick={openLogsDrawer} disabled={logsDrawer.loading} data-testid="passport-header-history">여권 히스토리</Button>, historyActionTarget)}
      <div className="passport-register-card">
        <div className="passport-register-intro">
          <div className="passport-register-title">
            <SafetyCertificateOutlined /> 새 여권 발급
          </div>
          <Text type="secondary">결제가 완료된 가족에게 새 여권을 발급합니다.</Text>
        </div>
        <div className="passport-register-fields">
          <div className="passport-form-field">
                <div className="passport-form-label">대상</div>
                <Select
                  style={{ width: '100%' }}
                  value={newOwnerId}
                  onChange={setNewOwnerId}
                  options={familyOptions}
                  placeholder="가족 선택"
                  popupClassName="passport-family-popup"
                  listHeight={288}
                  virtual={false}
                  inputReadOnly
                  showSearch={false}
                />
          </div>
          <div className="passport-form-field">
                <div className="passport-form-label">발급일</div>
                <DatePicker
                  popupClassName="passport-date-popup"
                  style={{ width: '100%' }}
                  value={newIssueDate}
                  onChange={(d) => setNewIssueDate(d || dayjs())}
                  allowClear={false}
                  inputReadOnly
                  disabledDate={(d) =>
                    !d || d.isAfter(dayjs(), 'day') || d.isBefore(dayjs().subtract(30, 'day'), 'day')
                  }
                />
          </div>
          <div className="passport-register-action">
            <Button
              className="passport-register-button"
              type="primary" size="large"
              icon={<SafetyCertificateOutlined />}
              loading={registering}
              disabled={!newOwnerId}
              onClick={confirmRegister}
            >
              여권 발급
            </Button>
          </div>
        </div>
        <div className="passport-register-note">발급일로부터 1년 유효 / 결제 완료 후 발급해 주세요.</div>
      </div>

      <div className="passport-section-heading">
        <div>
          <strong>보유 여권</strong>
          <span className="passport-family-summary">가족 <b>{targets.length}</b>명 · 전체 여권 <b>{cardPassports.length}</b>장</span>
        </div>
      </div>

      <div className="passport-grid-area">
        {loading && Object.keys(familyPassports).length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin tip="여권 로딩 중..." /></div>
        ) : cardPassports.length === 0 ? (
          <Empty description="등록된 여권이 없습니다 — 위에서 새 여권을 발급해 주세요" />
        ) : (
          <div className="fp-card-grid">
            {cardPassports.map(renderCard)}
          </div>
        )}
      </div>

      <Drawer
        rootClassName="passport-history-drawer tools-passport-history"
        open={logsDrawer.open}
        title={<h2 className="tools-history-title">여권 히스토리</h2>}
        placement="right" width={680} closable={false}
        maskClosable={!logsDrawer.loading} keyboard={!logsDrawer.loading}
        extra={<div className="passport-history-header-actions"><Button className="tools-history-refresh" icon={<ReloadOutlined />} disabled={logsDrawer.loading} onClick={openLogsDrawer}>새로고침</Button><Button className="tools-history-close" disabled={logsDrawer.loading} onClick={() => setLogsDrawer(prev => ({...prev,open:false}))}>닫기</Button></div>}
        onClose={() => { if (!logsDrawer.loading) setLogsDrawer(prev => ({...prev,open:false})); }}
      >
        <div className="tools-history-tools passport-history-overview">
          <div className="passport-history-summary-cards" aria-label="여권 이력 요약">
            <div className="passport-history-summary-card"><span>전체 여권</span><strong>{new Set(targets.flatMap(m => (familyPassports[m.member_id] || []).map(p => p.passport_id))).size}<small>장</small></strong></div>
            <div className="passport-history-summary-card"><span>할당 이력</span><strong>{logsDrawer.logs.length}<small>건</small></strong></div>
          </div>
        </div>
        <div className="tools-history-content">
        {logsDrawer.loading && <Spin />}
        {logsDrawer.error && <Text type="danger">{logsDrawer.error}</Text>}
        {!logsDrawer.loading && !logsDrawer.error && (() => {
          const passports = targets.flatMap(m => (familyPassports[m.member_id] || []).map(p => ({...p,owner_name:getDisplayName(m)})));
          const rows = passportHistoryRows(passports, logsDrawer.logs || []);
          if (!rows.length) return <Empty description="여권 기록이 없습니다" />;
          return (
            <table className="passport-event-table">
              <thead><tr><th scope="col">상태</th><th scope="col">대상자 / 이동</th><th scope="col">지점</th><th scope="col">발생일</th></tr></thead>
              <tbody>{rows.map(row => <tr key={row.key}>
                <td><span className={'passport-event-badge is-' + row.kind}>{row.label}</span></td>
                <td className="passport-event-target">{row.target}</td>
                <td>{row.store.replace(/^주렁주렁\s*/, '')}</td>
                <td className="passport-event-date">{row.occurredAt && !Number.isNaN(new Date(row.occurredAt).getTime()) ? formatDate(row.occurredAt) : '일자 미확인'}</td>
              </tr>)}</tbody>
            </table>
          );
        })()}
        </div>
      </Drawer>
    </div>
  );
}
